// This file is a COPY of an original file from mon-pix.
// If you need a change, modify the original file and
// propagate the changes in the copies in all the fronts.

import Service, { service } from '@ember/service';
import ENV from 'pix-orga/config/environment';

const { DEFAULT_LOCALE } = ENV.APP;

// Don't use directly this service, use a url service which extends it
export default class UrlBaseService extends Service {
  @service currentDomain;
  @service locale;

  get homeUrl() {
    const currentLocale = this.locale.currentLocale;
    return `${ENV.rootURL}?lang=${currentLocale}`;
  }

  get serverStatusUrl() {
    return `https://status.pix.org/?locale=${this.locale.currentLanguage}`;
  }

  get pixAppUrl() {
    if (!ENV.APP.PIX_APP_URL_WITHOUT_EXTENSION) return ENV.rootURL;
    const tld = this.currentDomain.getExtension();
    return `${ENV.APP.PIX_APP_URL_WITHOUT_EXTENSION}${tld}`;
  }

  get pixAppForgottenPasswordUrl() {
    const url = `${this.pixAppUrl}/mot-de-passe-oublie`;
    const currentLocale = this.locale.currentLocale;
    return this.currentDomain.isFranceDomain ? url : `${url}?lang=${currentLocale}`;
  }

  getPixWebsiteUrl(pathname = '') {
    const locale = this.currentDomain.isFranceDomain ? 'fr-FR' : this.locale.currentLocale;
    const rootUrl = PIX_WEBSITE_ROOT_URLS[locale] || PIX_WEBSITE_ROOT_URLS[DEFAULT_LOCALE];
    return pathname ? `${rootUrl}/${pathname}` : rootUrl;
  }

  getPixWebsiteUrlFor(pathKey) {
    const locale = this.currentDomain.isFranceDomain ? 'fr-FR' : this.locale.currentLocale;
    const pathsByLocale = PIX_WEBSITE_PATHS[pathKey] || {};
    const pathname = pathsByLocale[locale] || pathsByLocale[DEFAULT_LOCALE];
    return this.getPixWebsiteUrl(pathname);
  }
}

// Pix website URLs for each supported locales
export const PIX_WEBSITE_ROOT_URLS = {
  'de-AT': 'https://pix.org/en',
  'fr-FR': 'https://pix.fr',
  'fr-BE': 'https://pix.org/fr-be',
  'nl-BE': 'https://pix.org/nl-be',
  nl: 'https://pix.org/nl-be',
  en: 'https://pix.org/en',
  es: 'https://pix.org/en',
  'es-419': 'https://pix.org/en',
  it: 'https://pix.org/en',
  fr: 'https://pix.org/fr',
};

// Pix website paths for each supported locales
export const PIX_WEBSITE_PATHS = {
  CGU: {
    'de-AT': 'terms-and-conditions',
    'fr-FR': 'conditions-generales-d-utilisation',
    'fr-BE': 'conditions-generales-d-utilisation',
    'nl-BE': 'algemene-gebruiksvoorwaarden',
    nl: 'algemene-gebruiksvoorwaarden',
    en: 'terms-and-conditions',
    es: 'terms-and-conditions',
    'es-419': 'terms-and-conditions',
    it: 'terms-and-conditions',
    fr: 'conditions-generales-d-utilisation',
  },
  LEGAL_NOTICE: {
    'de-AT': 'legal-notice',
    'fr-FR': 'mentions-legales',
    'fr-BE': 'mentions-legales',
    'nl-BE': 'wettelijke-vermeldingen',
    nl: 'wettelijke-vermeldingen',
    en: 'legal-notice',
    es: 'legal-notice',
    'es-419': 'legal-notice',
    it: 'legal-notice',
    fr: 'mentions-legales',
  },
  DATA_PROTECTION_POLICY: {
    'de-AT': 'personal-data-protection-policy',
    'fr-FR': 'politique-protection-donnees-personnelles-app',
    'fr-BE': 'politique-protection-donnees-personnelles-app',
    'nl-BE': 'beleid-inzake-de-bescherming-van-persoonsgegevens',
    nl: 'beleid-inzake-de-bescherming-van-persoonsgegevens',
    en: 'personal-data-protection-policy',
    es: 'personal-data-protection-policy',
    'es-419': 'personal-data-protection-policy',
    it: 'personal-data-protection-policy',
    fr: 'politique-protection-donnees-personnelles-app',
  },
  ACCESSIBILITY: {
    'de-AT': 'accessibility',
    'fr-FR': 'accessibilite',
    'fr-BE': 'accessibilite',
    'nl-BE': 'toegankelijkheid',
    nl: 'toegankelijkheid',
    en: 'accessibility',
    es: 'accessibility',
    'es-419': 'accessibility',
    it: 'accessibility',
    fr: 'accessibilite',
  },
  ACCESSIBILITY_ORGA: {
    'de-AT': 'accessibility-pix-orga',
    'fr-FR': 'accessibilite-pix-orga',
    'fr-BE': 'accessibilite-pix-orga',
    'nl-BE': 'toegankelijkheid-pix-orga',
    nl: 'toegankelijkheid-pix-orga',
    en: 'accessibility-pix-orga',
    es: 'accessibility-pix-orga',
    'es-419': 'accessibility-pix-orga',
    it: 'accessibility-pix-orga',
    fr: 'accessibilite-pix-orga',
  },
  ACCESSIBILITY_CERTIF: {
    'de-AT': 'accessibility-pix-certif',
    'fr-FR': 'accessibilite-pix-certif',
    'fr-BE': 'accessibilite-pix-certif',
    'nl-BE': 'toegankelijkheid-pix-certif',
    nl: 'toegankelijkheid-pix-certif',
    en: 'accessibility-pix-certif',
    es: 'accessibility-pix-certif',
    'es-419': 'accessibility-pix-certif',
    it: 'accessibility-pix-certif',
    fr: 'accessibilite-pix-certif',
  },
  SUPPORT: {
    'de-AT': 'support',
    'fr-FR': 'support',
    'fr-BE': 'support',
    'nl-BE': 'support',
    nl: 'support',
    en: 'support',
    es: 'support',
    'es-419': 'support',
    it: 'support',
    fr: 'support',
  },
  SUPPORT_SCO: {
    'de-AT': 'support',
    'fr-FR': 'support/enseignement-scolaire',
    'fr-BE': 'support',
    'nl-BE': 'support',
    nl: 'support',
    en: 'support',
    es: 'support',
    'es-419': 'support',
    it: 'support',
    fr: 'support/enseignement-scolaire',
  },
  SUPPORT_SCO1D: {
    'de-AT': 'support',
    'fr-FR': 'support/enseignement-scolaire/1er-degre',
    'fr-BE': 'support',
    'nl-BE': 'support',
    nl: 'support',
    en: 'support',
    es: 'support',
    'es-419': 'support',
    it: 'support',
    fr: 'support/enseignement-scolaire/1er-degre',
  },
  CERTIFICATION_RESULTS_EXPLANATION: {
    'de-AT': 'understand-certification-results',
    'fr-FR': 'certification-comprendre-score-niveau',
    'fr-BE': 'certification-comprendre-score-niveau',
    'nl-BE': 'mijn-certificeringsresultaten-begrijpen',
    nl: 'mijn-certificeringsresultaten-begrijpen',
    en: 'understand-certification-results',
    es: 'understand-certification-results',
    'es-419': 'understand-certification-results',
    it: 'understand-certification-results',
    fr: 'certification-comprendre-score-niveau',
  },
  CERTIFICATION_HOW_TO: {
    'de-AT': 'certify-my-digital-skills',
    'fr-FR': 'se-certifier',
    'fr-BE': 'se-certifier',
    'nl-BE': 'mijn-digitale-vaardigheden-certificeren',
    nl: 'mijn-digitale-vaardigheden-certificeren',
    en: 'certify-my-digital-skills',
    es: 'certify-my-digital-skills',
    'es-419': 'certify-my-digital-skills',
    it: 'certify-my-digital-skills',
    fr: 'se-certifier',
  },
};
